// @ts-nocheck

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
// styles
import { SchoolConfigData } from '@jo-v1/components/ExitStrategy/type';
import ProductFlags from '@jo-v1/components/ProductFlags';
import ProductTags from '@jo-v1/components/ProductTags';
import { useEffect, useState, useMemo, memo, MouseEventHandler } from 'react';
import { FaStar } from 'react-icons/fa';
import { HiLocationMarker } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { ApplicationCta } from '@core/blocks/best-match-experience/types';
import LeadSubmittedCta from '@core/blocks/eduResults/components/LeadSubmittedCta';
import useProductEvents from '@core/hooks/cohesion/useProductEvents';
// selectors, actions
import {
  selectRecommenderPopupListId,
  setHeclidMapEntry,
  setProductCorrelationIdMapEntry,
  setViewCorrelationIdMapEntry,
  selectHeclidMap,
  selectProductCorrelationIdMap,
} from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import {
  setLeadDelivery,
  selectLeadDelivery,
  setCurrentProgram,
  selectSubmissions,
  setShowDuplicatePopup,
  addSubmission,
} from '@core/reducers/matchesSlice';
import { mobiusGetApplicationCta, mobiusRedirect } from '@core/services/mobius';
import { getSchoolConfig } from '@core/services/schoolConfig';
import { VoyagerResult } from '@core/ts/results';
// hooks & utils
import cn from '@core/utils/classNames';
import getToken from '@core/utils/getToken';

import SchoolProgramTabs from '../SchoolProgramTabs';
import styles from './RecommenderCard.module.css';

type RecommenderCardProps = {
  result: VoyagerResult;
  setRecommenderStep?: (step: string) => void;
  position?: number;
  setLoading?: (loading: boolean) => void;
  eventingOverrides?: any;
  index?: number;
  withImage?: boolean;
  withTopBorder?: boolean;
  fullWidth?: boolean;
  onRecommender?: boolean;
};

const RecommenderCard = ({
  result,
  setRecommenderStep,
  position,
  setLoading,
  eventingOverrides,
  index,
  withImage = false,
  fullWidth = false,
  withTopBorder = true,
  onRecommender = true,
}: RecommenderCardProps) => {
  const { currentPrograms } = useSelector(selectLeadDelivery);

  // dispatches actions
  const dispatch = useDispatch();

  // local state
  const [schoolConfig, setSchoolConfig] = useState<SchoolConfigData>({});
  const [imageErrored, setImageErrored] = useState(false);
  const [imageSource, setImageSource] = useState(schoolConfig?.image);
  const [applicationCtaData, setApplicationCtaData] = useState<{ label: string; url: string } | null>(null);

  // inputs state properties
  const inputs = useSelector(selectAllInputs);

  // Eventing Ids
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);

  // Store
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  // determine if we are in single submission
  // DEV NOTE: setting this to just be true for the purposes of a desired optimization
  // and we don't want to remove the multi-select code just yet
  const isSingleSubmissionRecommender = true;

  // Initialize events
  const { productLoaded, productViewed, productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location: eventingOverrides?.location,
      formatType: isSingleSubmissionRecommender ? 'app' : 'multi-program',
      formatSubtype: eventingOverrides?.formatSubtype || 'recommendation',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });

  // Fire productLoaded + productViewed events and adds entry to the viewCorrelationIdMap
  useEffect(() => {
    productLoaded();
    productViewed();
    if (result?.program?.id) {
      dispatch(
        setViewCorrelationIdMapEntry({
          key: result.program.id,
          value: newViewCorrelationId,
        })
      );
    }
  }, []);

  useEffect(() => {
    const getConfig = async () => {
      const schoolConfig = await getSchoolConfig(result?.school.id);
      setSchoolConfig(schoolConfig);
    };
    getConfig();
  }, [result]);

  useEffect(() => {
    if (schoolConfig?.image && !imageErrored) {
      setImageSource(schoolConfig.image);
    } else {
      setImageSource(
        'https://res.cloudinary.com/highereducation/image/upload/f_auto,fl_lossy,q_auto/v1/hosted-lead-flow/generic/hero.jpg'
      );
    }
  }, [schoolConfig, imageErrored]);

  const onBtnClickHandler = async (event) => {
    // if (!event.target.classList.contains('tab-trigger')) {
    setLoading(true);
    dispatch(setCurrentProgram(result));

    const newProductCorrelationId = uuid();

    // add correlationId to map for LDS lead submission
    if (result?.program?.id) {
      dispatch(
        setProductCorrelationIdMapEntry({
          key: result.program.id,
          value: newProductCorrelationId,
        })
      );
    }

    productClicked({
      correlationId: newProductCorrelationId,
    });

    // Mobius Redirect will fire the OutcomeTracked APP_VIEWED event
    const { heclid } = await mobiusRedirect({
      idToken: getToken(),
      productCorrelationId: newProductCorrelationId,
      link: result?.url,
      trackingContextOverride: {
        formatType: 'app',
        formatSubtype: 'recommendation',
      },
    });
    // add heclid to map for Mobius track if API call succeeds
    if (result?.program?.id) {
      dispatch(setHeclidMapEntry({ key: result.program.id, value: heclid }));
    }

    setLoading(false);
    setRecommenderStep('PII_CONFIRMATION');
  };

  // Matches State properties
  const submissions = useSelector(selectSubmissions);
  const isProgramSubmitted = submissions.some((s) => s.program.id === result.program.id);
  const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result?.school.id);
  const handleProductClick = () => {
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: true,
          currentPrograms: [result],
          showExitStep: true,
        })
      );
      dispatch(addSubmission({ ...result, duplicate: true }));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    const productCorrelationId = uuid();
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));
    productClicked({ correlationId: productCorrelationId });

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };

  useEffect(() => {
    const getApplicationCtaData = async () => {
      const response = await mobiusGetApplicationCta({
        programId: `${result.program.id}`,
        heclid: heclidMap[result.program.id],
        correlationId: productCorrelationIdMap[result.program.id],
      });
      setApplicationCtaData({ label: response?.label, url: response?.url });
    };

    getApplicationCtaData();
  }, []);

  return (
    <section className={cn(styles.card, fullWidth ? styles.fullWidthCard : undefined)}>
      {withTopBorder && (
        <div className={styles.cardTopBorder}>
          <div className={styles.bestMatchWrapper}>
            {index === 0 && (
              <span className={styles.starIconContainer}>
                <FaStar size={11} color="#FFCC00" />
              </span>
            )}
            {index === 0 ? 'Best Match' : 'Great Match'}
          </div>
          <ProductTags importantFactors={inputs.importantFactors} />
        </div>
      )}
      {withImage && schoolConfig?.image && (
        <img
          className={styles.heroImage}
          src={imageSource}
          alt={result?.school.name}
          onError={() => setImageErrored(true)}
        />
      )}
      <div className={styles.bodyContainer}>
        <div className={styles.cardHeader}>
          <div className={styles.logo}>
            <img
              src={
                result?.school?.slug
                  ? `https://simple-storage-server.highereducation.com/${result?.school.slug}.png?w=130&h=80`
                  : undefined
              }
              alt={result?.school?.name}
            />
          </div>
          <div>
            <h4 className={styles.title}>{result?.school?.name}</h4>
            {result?.school?.city && result?.school?.state && (
              <div className={styles.location}>
                <span className={styles.locationIcon}>
                  <HiLocationMarker size={16} />
                </span>{' '}
                {result?.school.city}, {result?.school.state}
              </div>
            )}
          </div>
        </div>
        <div className={styles.cardBody}>
          <h3 className={styles.programName}>{result?.program?.name}</h3>
          <ProductFlags flags={result?.school?.additional} />
          {!isProgramSubmitted ? (
            <button
              onClick={
                isSingleSubmissionRecommender && !isSchoolSubmitted && onRecommender
                  ? onBtnClickHandler
                  : handleProductClick
              }
              data-testid={`recommender-popup-card-button-${position}`}
              type="button"
              className={styles.cardBtnCta}
            >
              Explore Program
            </button>
          ) : (
            <button
              onClick={
                applicationCtaData?.url ? () => window.open(applicationCtaData?.url, '_blank') : handleProductClick
              }
              data-testid={`recommender-popup-card-button-${position}`}
              type="button"
              className={styles.cardBtnCtaSubmitted}
            >
              {applicationCtaData?.label || 'Start Your Application'}
            </button>
          )}
          <SchoolProgramTabs
            programSnippet={result?.program?.snippet}
            schoolAdditionalInfo={result?.school?.additional}
          />
        </div>
      </div>
    </section>
  );
};

// We use memo to prevent rerenders when props and state haven't changed
export default memo(RecommenderCard);
